import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import BasePage from './BasePage';
import {
    Typography,
    Card,
    Row,
    Col,
    Button,
    Modal,
    Form,
    Input,
    InputNumber,
    message,
    Tag,
    Space,
    Statistic,
    Select,
    Table,
    Tabs,
    Radio,
    Tooltip,
    Switch,
    Divider,
} from 'antd';
import {
    PlusOutlined,
    EnvironmentOutlined,
    EditOutlined,
    DeleteOutlined,
    EyeOutlined,
    ThunderboltOutlined,
    RiseOutlined,
    AppstoreOutlined,
    TableOutlined,
    SyncOutlined,
    DollarOutlined,
    ArrowRightOutlined,
    DisconnectOutlined,
    CloseCircleOutlined,
    ClockCircleOutlined,
    WarningOutlined,
    CarOutlined,
    BankOutlined,
    RightOutlined,
    TeamOutlined,
    HomeOutlined,
} from '@ant-design/icons';
import { sitesService, equipamentosService, analyticsService, empresaClientesService } from '../services/api';
import OperacaoSitesMap from '../components/MapComponent/OperacaoSitesMap';
import { useAutoRefresh } from '../hooks/useAutoRefresh';
import { useAuth } from '../hooks/useAuth';
import { 
    SiteWizard, 
    Step1SiteInfo, 
    Step2Solar, 
    Step3Bateria, 
    Step4Grid, 
    Step5Carregadores, 
    Step6Review 
} from '../components/SiteWizard';
import {
    getStatusColor,
    getStatusIcon,
    getStatusText,
    getPlantTypeIcon,
    getPlantTypeLabel,
    getBatteryIcon,
    getBatteryStatusIcon,
    getBatteryStatusText,
    formatPower,
    formatEnergy,
    formatCurrency,
} from '../utils/siteHelpers';

const { Title, Text } = Typography;
const { Option } = Select;
const { TabPane } = Tabs;

// Coordenadas fixas para sites conhecidos (evita imprecisão de geocoding)
const COORDS_FIXAS = {
    fiap:    { lat: -23.57428, lng: -46.62329 },
    quarahy: { lat: -22.91456, lng: -43.22432 },
};
function applyFixedCoords(site) {
    const nome = (site?.name || '').toLowerCase();
    if (nome.includes('quarahy')) return { ...site, latitude: COORDS_FIXAS.quarahy.lat, longitude: COORDS_FIXAS.quarahy.lng };
    if (nome.includes('fiap'))    return { ...site, latitude: COORDS_FIXAS.fiap.lat,    longitude: COORDS_FIXAS.fiap.lng    };
    return site;
}

const MapPage = () => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const [sites, setSites] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedSite, setSelectedSite] = useState(null);
    const [selectedMapGroup, setSelectedMapGroup] = useState(null); // empresaId selecionado para filtrar mapa
    const [siteEquipments, setSiteEquipments] = useState([]);
    const [createModalVisible, setCreateModalVisible] = useState(false);
    const [viewMode, setViewMode] = useState('table');
    const [filters, setFilters] = useState({
        state: null,
        status: null,
        plantType: null,
        hasBattery: null,
    });
    const [form] = Form.useForm();
    const [chargingStations, setChargingStations] = useState([]);
    const [erEquipamentos, setErEquipamentos] = useState([]);
    const [empresas, setEmpresas] = useState([]);

    const isAdmin = !user?.empresaClienteId;

    // Função de fetch com useCallback
    const fetchSites = useCallback(async () => {
        try {
            setLoading(true);
            const empresaId = user?.empresaClienteId || null;
            const response = await sitesService.getAllWithRealtime(empresaId);
            if (response.success) {
                setSites(response.data.map(applyFixedCoords));
            }
        } catch (error) {
            console.error('Erro ao buscar sites:', error);
            message.error('Erro ao carregar sites');
        } finally {
            setLoading(false);
        }
    }, [user]);


    const fetchChargingStations = useCallback(async () => {
        try {
            const data = await analyticsService.getChargingStations('week');
            setChargingStations(data?.stations || []);
        } catch (error) {
            console.error('Erro ao buscar estações de recarga:', error);
        }
    }, []);

    const fetchErEquipamentos = useCallback(async () => {
        try {
            const data = await analyticsService.getEvChargerLatest(null);
            setErEquipamentos(Array.isArray(data?.chargers) ? data.chargers : []);
        } catch (error) {
            console.error('Erro ao buscar ER equipamentos:', error);
        }
    }, []);

    useEffect(() => {
        fetchChargingStations();
        fetchErEquipamentos();
    }, [fetchChargingStations, fetchErEquipamentos]);

    // Auto-refresh hook
    const { isEnabled, setIsEnabled, lastUpdate, refresh } = useAutoRefresh(
        fetchSites,
        30000 // 30 segundos
    );

    useEffect(() => {
        fetchSites();
        if (isAdmin) {
            empresaClientesService.getAll()
                .then(data => {
                    const list = Array.isArray(data) ? data : (data?.data || []);
                    setEmpresas(list);
                })
                .catch(() => {});
        }
    }, [fetchSites, isAdmin]);

    // Estatísticas calculadas a partir dos dados reais
    // Status GoodWe SEMS (igual ao SEMS Plus): Funcionando(1), Aguardando(0), Offline(-1), Falha(-3), Construindo(3)
    // Status 2 (Alerta/Alarme) → agrupado em Falha | Status -2 (Sem Dispositivo) → agrupado em Offline
    const stats = useMemo(() => {
        const ps = (s) => parseInt(s.realtime?.plantStatus, 10);
        const activeSites   = sites.filter(s => ps(s) === 1).length;
        const waitingSites  = sites.filter(s => ps(s) === 0).length;
        // Offline inclui: -1 (desconectado) + -2 (sem dispositivo)
        const offlineSites  = sites.filter(s => ps(s) === -1 || ps(s) === -2).length;
        // Falha inclui: -3 (fault) + 2 (alarme/alerta)
        const faultSites    = sites.filter(s => ps(s) === -3 || ps(s) === 2).length;
        const buildingSites = sites.filter(s => ps(s) === 3).length;
        // Total = soma exata dos 5 grupos
        const knownStatuses = [1, 0, -1, -2, -3, 2, 3];
        const noStatusSites = sites.filter(s =>
            !s.realtime ||
            s.realtime.plantStatus == null ||
            !knownStatuses.includes(parseInt(s.realtime.plantStatus, 10))
        ).length;

        return {
            totalSites: sites.length,
            activeSites,
            offlineSites,
            faultSites,
            waitingSites,
            buildingSites,
            noStatusSites,
            totalPowerNow: sites.reduce((sum, s) => sum + (s.realtime?.pvPower || 0), 0) / 1000, // kW
            totalCapacity: Number(sites.reduce((sum, s) => sum + (Number(s.pvCapacity) || 0), 0)),
            todayGeneration: sites.reduce((sum, s) => sum + (s.realtime?.dailyGeneration || 0), 0),
            todayIncome: sites.reduce((sum, s) => sum + (s.realtime?.dailyIncome || 0), 0),
            statesCount: new Set(sites.map(s => s.state)).size,
        };
    }, [sites]);

    // Sites filtrados
    const filteredSites = useMemo(() => {
        return sites.filter(site => {
            if (filters.state && filters.state !== 'all' && site.state !== filters.state) {
                return false;
            }
            if (filters.status !== null && filters.status !== 'all') {
                const ps = parseInt(site.realtime?.plantStatus, 10);
                // Offline (-1) agrupa também Sem Dispositivo (-2)
                // Falha (-3) agrupa também Alerta (2)
                const match = filters.status === -1 ? (ps === -1 || ps === -2)
                            : filters.status === -3 ? (ps === -3 || ps === 2)
                            : ps === filters.status;
                if (!match) return false;
            }
            if (filters.plantType && filters.plantType !== 'all' && site.plantType !== filters.plantType) {
                return false;
            }
            if (filters.hasBattery !== null && filters.hasBattery !== 'all') {
                const hasBattery = (site.batteryCapacity && site.batteryCapacity > 0) ||
                    site.realtime?.batterySoc1 != null ||
                    (site.realtime?.batteryPower != null && site.realtime.batteryPower !== 0);
                if (filters.hasBattery === 'yes' && !hasBattery) return false;
                if (filters.hasBattery === 'no' && hasBattery) return false;
            }
            return true;
        });
    }, [sites, filters]);

    // Agrupamento por empresa (usado na view de cards para admin)
    const siteGroupsFiltered = useMemo(() => {
        const groups = {};
        for (const site of filteredSites) {
            const key = site.empresaClienteId || 'sem-empresa';
            if (!groups[key]) groups[key] = [];
            groups[key].push(site);
        }
        const entries = Object.entries(groups);
        entries.sort((a, b) => b[1].length - a[1].length);
        return entries;
    }, [filteredSites]);

    const getEmpresaNomeMap = useCallback((id) => {
        if (!id || id === 'sem-empresa') return 'Sem empresa';
        const emp = empresas.find(e => e.id === id);
        if (emp) return emp.nome;
        const sites = siteGroupsFiltered.find(([k]) => k === id)?.[1] || [];
        const nome = sites[0]?.name || '';
        if (nome.toLowerCase().includes('fiap')) return 'FIAP';
        if (nome.toLowerCase().includes('quarahy')) return 'Quarahy';
        return `Empresa ${String(id).slice(0, 8)}...`;
    }, [empresas, siteGroupsFiltered]);

    const handleSiteClick = async (site) => {
        setSelectedSite(site);
        try {
            // Verificação: se o ID é numérico (1, 2, 3), é site REAL da Lambda
            // Se o ID é UUID, é site MANUAL
            const isNumericId = !isNaN(Number(site.id)) && Number.isInteger(Number(site.id));
            
            console.log('🔍 MapPage - Site clicado:', site.id, 'É numérico?', isNumericId);
            
            if (isNumericId) {
                // Site REAL: buscar via API Goodwe
                console.log('🚀 MapPage - Buscando equipamentos de site REAL via /api/usinas/:id/devices');
                const response = await equipamentosService.getDevicesFromGoodwe(site.id);
                if (response.success && response.data) {
                    console.log('✅ MapPage - Equipamentos encontrados:', response.data.length);
                    setSiteEquipments(response.data);
                } else {
                    console.log('⚠️ MapPage - Nenhum equipamento retornado');
                    setSiteEquipments([]);
                }
            } else {
                // Site MANUAL: buscar da tabela equipamentos
                console.log('📦 MapPage - Buscando equipamentos de site MANUAL via /api/equipamentos/site/:id');
                const response = await equipamentosService.getBySite(site.id);
                if (response.success) {
                    setSiteEquipments(response.data);
                } else {
                    setSiteEquipments([]);
                }
            }
        } catch (error) {
            console.error('❌ Erro ao buscar equipamentos:', error);
            setSiteEquipments([]);
        }
    };

    const handleDeleteSite = (siteId) => {
        Modal.confirm({
            title: 'Confirmar exclusão',
            content: 'Tem certeza que deseja excluir este site?',
            okText: 'Sim',
            cancelText: 'Não',
            okButtonProps: { danger: true },
            onOk: async () => {
                try {
                    await sitesService.delete(siteId);
                    message.success('Site excluído com sucesso');
                    fetchSites();
                    if (selectedSite?.id === siteId) {
                        setSelectedSite(null);
                    }
                } catch (error) {
                    console.error('Erro ao excluir site:', error);
                    message.error('Erro ao excluir site');
                }
            },
        });
    };

    const handleCreateSite = () => {
        setCreateModalVisible(true);
    };

    const handleSubmitSite = async (completeFormData) => {
        try {
            // Preparar dados para envio ao backend
            const siteData = {
                // Site básico
                name: completeFormData.name,
                logradouro: completeFormData.logradouro,
                numero: completeFormData.numero,
                complemento: completeFormData.complemento,
                cep: completeFormData.cep,
                bairro: completeFormData.bairro,
                city: completeFormData.city,
                state: completeFormData.state,
                latitude: completeFormData.latitude,
                longitude: completeFormData.longitude,
                observacoes: completeFormData.observacoes,
                empresaClienteId: 'empresa-001', // Fixo para desenvolvimento

                // Solar/Inversor
                hasGoodwe: completeFormData.hasGoodwe,
                goodwePlantId: completeFormData.goodwePlantId,
                inversorNome: completeFormData.inversorNome,
                inversorSerial: completeFormData.inversorSerial,
                inversorFabricante: completeFormData.inversorFabricante,
                inversorModelo: completeFormData.inversorModelo,
                pvCapacity: completeFormData.pvCapacity,

                // Bateria
                hasBattery: completeFormData.hasBattery,
                batteryCapacity: completeFormData.batteryCapacity,
                bateriaFabricante: completeFormData.bateriaFabricante,
                bateriaGerenteNome: completeFormData.bateriaGerenteNome,
                bateriaGerenteEmail: completeFormData.bateriaGerenteEmail,
                bateriaSameAddress: completeFormData.bateriaSameAddress,
                bateriaLogradouro: completeFormData.bateriaLogradouro,
                bateriaNumero: completeFormData.bateriaNumero,
                bateriaCep: completeFormData.bateriaCep,
                bateriaBairro: completeFormData.bateriaBairro,
                bateriaCidade: completeFormData.bateriaCidade,

                // Grid
                gridConcessionaria: completeFormData.gridConcessionaria,
                gridNumeroInstalacao: completeFormData.gridNumeroInstalacao,
                // gridPdfFile será tratado em próxima fase (upload)

                // Carregadores
                hasChargers: completeFormData.hasChargers,
                carregadores: completeFormData.carregadores || [],
            };

            // Usar o novo endpoint /complete
            const response = await sitesService.createComplete(siteData);
            
            message.success(`Site criado com sucesso! ${response.data.summary.totalEquipamentos} equipamentos vinculados.`);
            setCreateModalVisible(false);
            fetchSites();
        } catch (error) {
            console.error('Erro ao criar site:', error);
            message.error(error.response?.data?.message || 'Erro ao criar site');
        }
    };

    const handleCancelWizard = () => {
        setCreateModalVisible(false);
    };

    // Status agregado de um grupo de usinas
    const getGroupStatus = (sitesDoGrupo) => {
        const statuses = sitesDoGrupo.map(s => parseInt(s.realtime?.plantStatus, 10));
        if (statuses.some(st => st === 1)) return 1;       // qualquer gerando → Gerando
        if (statuses.some(st => st === 0)) return 0;       // aguardando
        if (statuses.some(st => st === 2)) return 2;       // alerta
        if (statuses.some(st => st === -3)) return -3;     // falha
        if (statuses.some(st => st === -1)) return -1;     // offline
        return null;
    };

    // Dados da tabela em árvore: grupos como pais, usinas como filhos
    const treeTableData = useMemo(() => {
        if (!isAdmin || siteGroupsFiltered.length <= 1) {
            return filteredSites.map(s => ({ ...s, key: String(s.id) }));
        }
        return siteGroupsFiltered.map(([empresaId, sitesDoGrupo]) => {
            const nome = getEmpresaNomeMap(empresaId);
            const totalPower = sitesDoGrupo.reduce((sum, s) => sum + (s.realtime?.pvPower || 0), 0);
            const totalToday = sitesDoGrupo.reduce((sum, s) => sum + (s.realtime?.dailyGeneration || 0), 0);
            const totalIncome = sitesDoGrupo.reduce((sum, s) => sum + (s.realtime?.dailyIncome || 0), 0);
            const groupChargers = erEquipamentos.filter(er =>
                sitesDoGrupo.some(s => String(s.id) === String(er.siteId))
            );
            // Localização: cidades únicas das usinas do grupo
            const cidades = [...new Set(sitesDoGrupo.map(s => s.city).filter(Boolean))];
            const estados = [...new Set(sitesDoGrupo.map(s => s.state).filter(Boolean))];
            const localLabel = cidades.length > 0
                ? `${cidades.join(', ')} / ${estados.join(', ')}`
                : null;
            const groupStatus = getGroupStatus(sitesDoGrupo);
            return {
                key: `group-${empresaId}`,
                id: `group-${empresaId}`,
                empresaId,
                isGroup: true,
                name: nome,
                city: localLabel,
                state: '',
                realtime: {
                    pvPower: totalPower,
                    dailyGeneration: totalToday,
                    dailyIncome: totalIncome,
                    plantStatus: groupStatus,
                },
                _groupChargers: groupChargers,
                _numUsinas: sitesDoGrupo.length,
                children: sitesDoGrupo.map(s => ({ ...s, key: String(s.id) })),
            };
        });
    }, [isAdmin, siteGroupsFiltered, filteredSites, getEmpresaNomeMap, erEquipamentos]);

    // Colunas da tabela
    const tableColumns = [
        {
            title: 'Site / Usina',
            dataIndex: 'name',
            key: 'name',
            render: (name, record) => {
                if (record.isGroup) {
                    const n = record._numUsinas ?? record.children?.length ?? 0;
                    const firstChild = record.children?.[0];
                    return (
                        <Space>
                            <div style={{ width: 32, height: 32, borderRadius: '50%', background: '#e6f7ff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                <BankOutlined style={{ color: '#1890ff', fontSize: 16 }} />
                            </div>
                            {firstChild ? (
                                <Text strong style={{ fontSize: 15 }}>
                                    {name}
                                </Text>
                            ) : (
                                <Text strong style={{ fontSize: 15, color: '#1890ff' }}>{name}</Text>
                            )}
                            <Tag color="blue" style={{ fontSize: 11 }}>{n} {n === 1 ? 'usina' : 'usinas'}</Tag>
                        </Space>
                    );
                }
                return (
                    <Space>
                        <span style={{ fontSize: '18px' }}>{getPlantTypeIcon(record.plantType)}</span>
                        <Text>{name}</Text>
                    </Space>
                );
            },
            sorter: (a, b) => a.name.localeCompare(b.name),
        },
        {
            title: 'Status',
            key: 'status',
            render: (_, record) => {
                const ps = record.realtime?.plantStatus;
                const statusText = getStatusText(ps) || record.realtime?.plantStatusText || '—';
                const tooltipTitle =
                    ps === 0 && record.realtime?.dailyGeneration > 0
                        ? 'Conectado. Não gerando no momento. Gerou hoje.'
                        : ps === 1 ? 'Gerando energia'
                        : ps === -1 ? 'Desconectado'
                        : undefined;
                return (
                <Space direction="vertical" size="small">
                    <Tooltip title={tooltipTitle}>
                        <Tag color={getStatusColor(ps)}>
                            {getStatusIcon(ps)} {statusText}
                        </Tag>
                    </Tooltip>
                    {record.realtime?.batterySoc1 && (
                        <Tooltip title={`Bateria: ${record.realtime.batterySoc1}% - ${getBatteryStatusText(record.realtime.batteryStatus1)}`}>
                            <Text style={{ fontSize: '12px' }}>
                                {getBatteryIcon(record.realtime.batterySoc1)} {record.realtime.batterySoc1}%
                                {' '}{getBatteryStatusIcon(record.realtime.batteryStatus1)}
                            </Text>
                        </Tooltip>
                    )}
                </Space>
                );
            },
            filters: [
                { text: 'Gerando', value: 1 },
                { text: 'Aguardando', value: 0 },
                { text: 'Offline', value: -1 },
                { text: 'Sem Dispositivo', value: -2 },
                { text: 'Falha', value: -3 },
            ],
            onFilter: (value, record) => record.realtime?.plantStatus === value,
        },
        {
            title: 'Tipo',
            dataIndex: 'plantType',
            key: 'plantType',
            render: (type, record) => record.isGroup ? '—' : (getPlantTypeLabel(type) || '—'),
        },
        {
            title: 'PV Agora',
            key: 'pvPower',
            align: 'right',
            render: (_, record) => (
                <Text strong style={{ color: '#FFB100' }}>
                    {formatPower(record.realtime?.pvPower)}
                </Text>
            ),
            sorter: (a, b) => (a.realtime?.pvPower || 0) - (b.realtime?.pvPower || 0),
        },
        {
            title: 'Carga',
            key: 'loadPower',
            align: 'right',
            render: (_, record) => formatPower(record.realtime?.loadPower),
        },
        {
            title: 'Hoje',
            key: 'dailyGeneration',
            align: 'right',
            render: (_, record) => (
                <Text strong style={{ color: '#52c41a' }}>
                    {formatEnergy(record.realtime?.dailyGeneration)}
                </Text>
            ),
            sorter: (a, b) => (a.realtime?.dailyGeneration || 0) - (b.realtime?.dailyGeneration || 0),
        },
        {
            title: 'Receita Hoje',
            key: 'dailyIncome',
            align: 'right',
            render: (_, record) => formatCurrency(record.realtime?.dailyIncome),
            sorter: (a, b) => (a.realtime?.dailyIncome || 0) - (b.realtime?.dailyIncome || 0),
        },
        {
            title: 'Local',
            key: 'location',
            render: (_, record) => {
                if (record.isGroup) {
                    // city já contém o label formatado ("São Paulo / SP")
                    return record.city
                        ? <Text type="secondary" style={{ fontSize: 12 }}>{record.city}</Text>
                        : <Text type="secondary">—</Text>;
                }
                const city = record.city || null;
                const state = record.state || null;
                if (!city && !state) return <Text type="secondary">—</Text>;
                return `${city || ''}${city && state ? ' - ' : ''}${state || ''}`;
            },
        },
        {
            title: 'Recarga EV',
            key: 'recarga',
            align: 'center',
            render: (_, record) => {
                if (record.isGroup) {
                    const n = record._groupChargers?.length || 0;
                    if (n === 0) return <Text type="secondary" style={{ fontSize: 12 }}>—</Text>;
                    const totalEnergia = record._groupChargers.reduce((s, er) => s + (parseFloat(er.leitura?.energiaTotal || er.energiaTotal) || 0), 0);
                    return (
                        <Space direction="vertical" size={0} style={{ textAlign: 'center' }}>
                            <Tag icon={<CarOutlined />} color="cyan" style={{ fontSize: 11 }}>{n} carregador{n > 1 ? 'es' : ''}</Tag>
                            {totalEnergia > 0 && (
                                <Text style={{ fontSize: 11, color: '#08979c' }}>{totalEnergia.toFixed(1)} kWh</Text>
                            )}
                        </Space>
                    );
                }
                const chargers = erEquipamentos.filter(er => String(er.siteId) === String(record.id));
                if (chargers.length === 0) return <Text type="secondary" style={{ fontSize: 12 }}>—</Text>;
                return (
                    <Tag icon={<CarOutlined />} color="cyan" style={{ fontSize: 11 }}>
                        {chargers.length} carregador{chargers.length > 1 ? 'es' : ''}
                    </Tag>
                );
            },
        },
        {
            title: 'Ações',
            key: 'actions',
            render: (_, record) => {
                if (record.isGroup) {
                    const firstChild = record.children?.[0];
                    if (!firstChild) return null;
                    return (
                        <Button
                            type="link"
                            size="small"
                            icon={<EyeOutlined />}
                            onClick={() => navigate(`/site/${firstChild.id}`)}
                        >
                            Ver
                        </Button>
                    );
                }
                const isUsina = !isNaN(Number(record.id)) && Number.isInteger(Number(record.id));
                return (
                    <Space>
                        <Button
                            type="link"
                            size="small"
                            icon={<EyeOutlined />}
                            onClick={() => handleSiteClick(record)}
                        >
                            Ver
                        </Button>
                        {!isUsina && (
                            <Button
                                type="link"
                                size="small"
                                danger
                                icon={<DeleteOutlined />}
                                onClick={() => handleDeleteSite(record.id)}
                            >
                                Excluir
                            </Button>
                        )}
                    </Space>
                );
            },
        },
    ];

    // Estados únicos para filtro
    const uniqueStates = useMemo(() => {
        return [...new Set(sites.map(s => s.state))].sort();
    }, [sites]);

    // Sites para o mapa: filtra pelo grupo selecionado (ou mostra todos)
    const sitesForMap = useMemo(() => {
        if (!selectedMapGroup) return filteredSites;
        const group = siteGroupsFiltered.find(([k]) => k === selectedMapGroup);
        return group ? group[1] : filteredSites;
    }, [selectedMapGroup, filteredSites, siteGroupsFiltered]);

    const mapGroupLabel = useMemo(() => {
        if (!selectedMapGroup) return null;
        return getEmpresaNomeMap(selectedMapGroup);
    }, [selectedMapGroup, getEmpresaNomeMap]);

    return (
        <BasePage>
            <div style={{ backgroundColor: '#FFF', padding: '24px', borderRadius: '12px' }}>
                {/* Header */}
                <div style={{ marginBottom: '24px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '16px' }}>
                        <Title level={2} style={{ margin: 0 }}>
                            <EnvironmentOutlined style={{ marginRight: '12px', color: '#FB8500' }} />
                            Mapa dos Sites
                        </Title>
                        <Space wrap>
                            <Text type="secondary" style={{ fontSize: '12px' }}>
                                Atualizado: {lastUpdate}
                            </Text>
                            <Switch
                                checked={isEnabled}
                                onChange={setIsEnabled}
                                checkedChildren="Auto"
                                unCheckedChildren="Manual"
                            />
                            <Button
                                icon={<SyncOutlined spin={loading} />}
                                onClick={refresh}
                                loading={loading}
                            >
                                Atualizar
                            </Button>
                            <Button
                                type="primary"
                                icon={<PlusOutlined />}
                                onClick={handleCreateSite}
                                style={{ backgroundColor: '#FB8500', borderColor: '#FB8500' }}
                            >
                                Novo Site
                            </Button>
                        </Space>
                    </div>

                    {/* Cards de Métricas Principais */}
                    <Row gutter={[16, 16]} style={{ marginBottom: '16px' }}>
                        <Col xs={24} sm={12} lg={6}>
                            <Card>
                                <Statistic
                                    title="Total de Usinas"
                                    value={stats.totalSites}
                                    prefix={<EnvironmentOutlined />}
                                    valueStyle={{ color: '#1890ff' }}
                                />
                                <Text type="secondary" style={{ fontSize: '11px' }}>
                                    em {stats.statesCount} {stats.statesCount === 1 ? 'estado' : 'estados'}
                                </Text>
                            </Card>
                        </Col>
                        <Col xs={24} sm={12} lg={6}>
                            <Card>
                                <Statistic
                                    title="Gerando Agora"
                                    value={stats.totalPowerNow}
                                    suffix="kW"
                                    precision={1}
                                    prefix={<ThunderboltOutlined />}
                                    valueStyle={{ color: '#52c41a' }}
                                />
                                <Text type="secondary" style={{ fontSize: '11px' }}>
                                    Capacidade: {Number(stats.totalCapacity || 0).toFixed(1)} kW
                                </Text>
                            </Card>
                        </Col>
                        <Col xs={24} sm={12} lg={6}>
                            <Card>
                                <Statistic
                                    title="Geração Hoje"
                                    value={stats.todayGeneration}
                                    suffix="kWh"
                                    precision={1}
                                    prefix={<RiseOutlined />}
                                    valueStyle={{ color: '#FFB100' }}
                                />
                            </Card>
                        </Col>
                        <Col xs={24} sm={12} lg={6}>
                            <Card>
                                <Statistic
                                    title="Receita Hoje"
                                    value={stats.todayIncome}
                                    prefix={<DollarOutlined />}
                                    precision={2}
                                    valueStyle={{ color: '#52c41a' }}
                                />
                            </Card>
                        </Col>
                    </Row>

                    {/* Status Operacional */}
                    <Card 
                        title={
                            <Space>
                                <ThunderboltOutlined style={{ color: '#FB8500' }} />
                                <Text strong>Status Operacional</Text>
                            </Space>
                        }
                        style={{ marginBottom: '16px' }}
                    >
                        {/* 5 status idênticos ao SEMS Plus */}
                        <Row gutter={[12, 12]}>
                            {[
                                { label: 'Funcionando', value: 1,  bg: '#f6ffed', border: '#52c41a',  color: '#52c41a',  icon: <ThunderboltOutlined /> },
                                { label: 'Aguardando',  value: 0,  bg: '#e6f7ff', border: '#1890ff',  color: '#1890ff',  icon: <ClockCircleOutlined /> },
                                { label: 'Offline',     value: -1, bg: '#fafafa', border: '#8c8c8c',  color: '#8c8c8c',  icon: <DisconnectOutlined /> },
                                { label: 'Falha',       value: -3, bg: '#fff1f0', border: '#ff4d4f',  color: '#ff4d4f',  icon: <CloseCircleOutlined /> },
                                { label: 'Construindo', value: 3,  bg: '#e6fffb', border: '#13c2c2',  color: '#13c2c2',  icon: <HomeOutlined /> },
                            ].map(({ label, value, bg, border, color, icon }) => {
                                const count = value === -1  ? stats.offlineSites
                                            : value === -3  ? stats.faultSites
                                            : value === 1   ? stats.activeSites
                                            : value === 0   ? stats.waitingSites
                                            : stats.buildingSites;
                                const active = filters.status === value;
                                return (
                                    <Col flex="1 1 120px" key={value} style={{ minWidth: 110 }}>
                                        <div
                                            onClick={() => setFilters({ ...filters, status: active ? 'all' : value })}
                                            style={{
                                                textAlign: 'center', padding: '16px',
                                                backgroundColor: bg, borderRadius: '8px',
                                                border: active ? `2px solid ${border}` : `1px solid ${border}44`,
                                                cursor: 'pointer', transition: 'all 0.2s',
                                                boxShadow: active ? `0 0 0 3px ${border}22` : 'none',
                                            }}
                                        >
                                            {React.cloneElement(icon, { style: { fontSize: 30, color, marginBottom: 6 } })}
                                            <Statistic value={count} valueStyle={{ color, fontSize: 26, fontWeight: 700 }} />
                                            <Text type="secondary" style={{ fontSize: 12 }}>{label}</Text>
                                        </div>
                                    </Col>
                                );
                            })}
                        </Row>
                        {stats.noStatusSites > 0 && (
                            <div style={{ marginTop: '12px', textAlign: 'center' }}>
                                <Text type="secondary" style={{ fontSize: '11px' }}>
                                    ⚠️ {stats.noStatusSites} usina{stats.noStatusSites > 1 ? 's' : ''} com status não identificado (verifique a integração GoodWe)
                                </Text>
                            </div>
                        )}
                        {filters.status !== null && filters.status !== 'all' && (
                            <div style={{ marginTop: '12px', textAlign: 'center' }}>
                                <Button 
                                    size="small" 
                                    type="link" 
                                    onClick={() => setFilters({ ...filters, status: 'all' })}
                                >
                                    Limpar filtro de status
                                </Button>
                            </div>
                        )}
                    </Card>
                </div>

                {/* Filtros */}
                <Card style={{ marginBottom: '16px' }} title="Filtros">
                    <Row gutter={16}>
                        <Col xs={24} sm={12} md={6}>
                            <Text strong>Status:</Text>
                            <Select
                                style={{ width: '100%', marginTop: '8px' }}
                                placeholder="Todos"
                                value={filters.status}
                                onChange={(value) => setFilters({ ...filters, status: value })}
                            >
                                <Option value="all">Todos</Option>
                                <Option value={1}>Funcionando</Option>
                                <Option value={0}>Aguardando</Option>
                                <Option value={-1}>Offline</Option>
                                <Option value={-3}>Falha</Option>
                                <Option value={3}>Construindo</Option>
                            </Select>
                        </Col>
                        <Col xs={24} sm={12} md={6}>
                            <Text strong>Tipo:</Text>
                            <Select
                                style={{ width: '100%', marginTop: '8px' }}
                                placeholder="Todos"
                                value={filters.plantType}
                                onChange={(value) => setFilters({ ...filters, plantType: value })}
                            >
                                <Option value="all">Todos</Option>
                                <Option value="residential">Residencial</Option>
                                <Option value="commercial">Comercial</Option>
                                <Option value="storage">Armazenamento</Option>
                                <Option value="ground-mounted">Solo</Option>
                            </Select>
                        </Col>
                        <Col xs={24} sm={12} md={6}>
                            <Text strong>Estado:</Text>
                            <Select
                                style={{ width: '100%', marginTop: '8px' }}
                                placeholder="Todos"
                                value={filters.state}
                                onChange={(value) => setFilters({ ...filters, state: value })}
                            >
                                <Option value="all">Todos</Option>
                                {uniqueStates.map(state => (
                                    <Option key={state} value={state}>{state}</Option>
                                ))}
                            </Select>
                        </Col>
                        <Col xs={24} sm={12} md={6}>
                            <Text strong>Bateria:</Text>
                            <Select
                                style={{ width: '100%', marginTop: '8px' }}
                                placeholder="Todos"
                                value={filters.hasBattery}
                                onChange={(value) => setFilters({ ...filters, hasBattery: value })}
                            >
                                <Option value="all">Todos</Option>
                                <Option value="yes">Com Bateria</Option>
                                <Option value="no">Sem Bateria</Option>
                            </Select>
                        </Col>
                    </Row>
                </Card>

                {/* Tabela com agrupamento em árvore */}
                <div style={{ marginBottom: '12px', display: 'flex', justifyContent: 'flex-end' }}>
                    <Text type="secondary" style={{ fontSize: 13 }}>
                        <TableOutlined style={{ marginRight: 6 }} />
                        Exibindo {filteredSites.length} de {sites.length} sites
                    </Text>
                </div>

                <Table
                    columns={tableColumns}
                    dataSource={treeTableData}
                    rowKey="key"
                    loading={loading}
                    pagination={{
                        pageSize: 20,
                        showSizeChanger: true,
                        showTotal: (total) => `Total: ${total} grupos/sites`,
                    }}
                    expandable={{
                        defaultExpandAllRows: true,
                        rowExpandable: (record) => record.isGroup && record.children?.length > 0,
                        expandIcon: ({ expanded, onExpand, record }) => {
                            if (!record.isGroup || !record.children?.length) return null;
                            return (
                                <RightOutlined
                                    onClick={e => onExpand(record, e)}
                                    style={{
                                        marginRight: 8,
                                        fontSize: 11,
                                        color: '#1890ff',
                                        cursor: 'pointer',
                                        transition: 'transform 0.2s',
                                        transform: expanded ? 'rotate(90deg)' : 'rotate(0deg)',
                                        display: 'inline-block',
                                    }}
                                />
                            );
                        },
                    }}
                    rowClassName={(record) => {
                        if (record.isGroup) return 'group-row';
                        return '';
                    }}
                    onRow={(record) => ({
                        onClick: () => {
                            if (record.isGroup) {
                                // Clique no grupo → filtrar mapa (toggle)
                                setSelectedMapGroup(prev =>
                                    prev === record.empresaId ? null : record.empresaId
                                );
                            }
                        },
                        style: record.isGroup ? { cursor: 'pointer' } : {},
                    })}
                />

                {/* ── Mapa das Usinas ── */}
                <Divider style={{ margin: '28px 0 16px' }} />
                <div style={{ marginBottom: 32 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                        <Space>
                            <EnvironmentOutlined style={{ color: '#FB8500', fontSize: 18 }} />
                            <Title level={4} style={{ margin: 0 }}>
                                Mapa das Usinas
                                {mapGroupLabel && (
                                    <Tag color="blue" style={{ marginLeft: 10, fontWeight: 400, fontSize: 13 }}>
                                        {mapGroupLabel}
                                    </Tag>
                                )}
                            </Title>
                        </Space>
                        {selectedMapGroup && (
                            <Button
                                size="small"
                                onClick={() => setSelectedMapGroup(null)}
                                type="link"
                            >
                                Mostrar todas as usinas
                            </Button>
                        )}
                    </div>

                    {/* Legenda */}
                    <Space style={{ marginBottom: 10 }}>
                        {[
                            { color: '#52c41a', label: 'Gerando' },
                            { color: '#1890ff', label: 'Aguardando' },
                            { color: '#ff4d4f', label: 'Offline' },
                        ].map(l => (
                            <Space key={l.label} size={4}>
                                <div style={{ width: 10, height: 10, borderRadius: '50%', background: l.color }} />
                                <Text type="secondary" style={{ fontSize: 12 }}>{l.label}</Text>
                            </Space>
                        ))}
                        {!selectedMapGroup && (
                            <Text type="secondary" style={{ fontSize: 12, marginLeft: 8 }}>
                                · Clique em um grupo na tabela para filtrar
                            </Text>
                        )}
                    </Space>

                    <OperacaoSitesMap
                        sites={sitesForMap}
                        onSiteClick={handleSiteClick}
                        highlightedSiteId={selectedSite?.id}
                        height={420}
                    />
                </div>

                <Divider style={{ margin: '0 0 24px' }} />

                {/* Modal de Detalhes do Site */}
                <Modal
                    title={
                        <Space>
                            <span style={{ fontSize: '24px' }}>{selectedSite && getPlantTypeIcon(selectedSite.plantType)}</span>
                            <Text strong>{selectedSite ? selectedSite.name : 'Detalhes do Site'}</Text>
                            {selectedSite?.realtime && (
                                <Tooltip
                                    title={
                                        selectedSite.realtime.plantStatus === 0 && selectedSite.realtime.dailyGeneration > 0
                                            ? 'Conectado. Não gerando no momento. Gerou hoje.'
                                            : selectedSite.realtime.plantStatus === 1
                                                ? 'Gerando energia'
                                                : selectedSite.realtime.plantStatus === -1
                                                    ? 'Desconectado'
                                                    : undefined
                                    }
                                >
                                    <Tag color={getStatusColor(selectedSite.realtime.plantStatus)}>
                                        {getStatusIcon(selectedSite.realtime.plantStatus)} {getStatusText(selectedSite.realtime.plantStatus)}
                                    </Tag>
                                </Tooltip>
                            )}
                        </Space>
                    }
                    open={!!selectedSite}
                    onCancel={() => setSelectedSite(null)}
                    footer={[
                        <Button key="close" onClick={() => setSelectedSite(null)}>
                            Fechar
                        </Button>,
                        <Button 
                            key="details" 
                            type="primary" 
                            icon={<ArrowRightOutlined />}
                            style={{ backgroundColor: '#FB8500', borderColor: '#FB8500' }}
                            onClick={() => {
                                navigate(`/site/${selectedSite.id}`);
                                setSelectedSite(null);
                            }}
                        >
                            Ver Dashboard Completo
                        </Button>,
                    ]}
                    width={900}
                >
                    {selectedSite && (
                        <>
                            {/* Quick Stats no topo */}
                            <Row gutter={[8, 8]} style={{ marginBottom: '16px' }}>
                                <Col span={6}>
                                    <Card size="small" bodyStyle={{ padding: '12px', textAlign: 'center', backgroundColor: '#f0f5ff' }}>
                                        <Text type="secondary" style={{ fontSize: '11px', display: 'block' }}>PV Agora</Text>
                                        <Text strong style={{ fontSize: '18px', color: '#1890ff' }}>
                                            {selectedSite.realtime?.pvPower ? (selectedSite.realtime.pvPower / 1000).toFixed(1) : '-'} kW
                                        </Text>
                                    </Card>
                                </Col>
                                <Col span={6}>
                                    <Card size="small" bodyStyle={{ padding: '12px', textAlign: 'center', backgroundColor: '#f6ffed' }}>
                                        <Text type="secondary" style={{ fontSize: '11px', display: 'block' }}>Hoje</Text>
                                        <Text strong style={{ fontSize: '18px', color: '#52c41a' }}>
                                            {selectedSite.realtime?.dailyGeneration?.toFixed(1) || '-'} kWh
                                        </Text>
                                    </Card>
                                </Col>
                                <Col span={6}>
                                    <Card size="small" bodyStyle={{ padding: '12px', textAlign: 'center', backgroundColor: '#fff7e6' }}>
                                        <Text type="secondary" style={{ fontSize: '11px', display: 'block' }}>Total</Text>
                                        <Text strong style={{ fontSize: '18px', color: '#FFB100' }}>
                                            {selectedSite.realtime?.totalGeneration?.toFixed(0) || '-'} kWh
                                        </Text>
                                    </Card>
                                </Col>
                                <Col span={6}>
                                    <Card size="small" bodyStyle={{ padding: '12px', textAlign: 'center', backgroundColor: '#f6ffed' }}>
                                        <Text type="secondary" style={{ fontSize: '11px', display: 'block' }}>Receita Hoje</Text>
                                        <Text strong style={{ fontSize: '16px', color: '#52c41a' }}>
                                            {formatCurrency(selectedSite.realtime?.dailyIncome)}
                                        </Text>
                                    </Card>
                                </Col>
                            </Row>

                            <Tabs defaultActiveKey="info">
                                <TabPane tab="📋 Informações" key="info">
                                    <Space direction="vertical" style={{ width: '100%' }} size="middle">
                                        <Card size="small" title="📍 Localização">
                                            <Row gutter={16}>
                                                <Col span={12}>
                                                    <p><strong>Endereço:</strong> {selectedSite.address}</p>
                                                    <p><strong>Cidade:</strong> {selectedSite.city}</p>
                                                    <p><strong>Estado:</strong> {selectedSite.state}</p>
                                                </Col>
                                                <Col span={12}>
                                                    {selectedSite.zipCode && <p><strong>CEP:</strong> {selectedSite.zipCode}</p>}
                                                    {selectedSite.latitude && <p><strong>Latitude:</strong> {selectedSite.latitude}</p>}
                                                    {selectedSite.longitude && <p><strong>Longitude:</strong> {selectedSite.longitude}</p>}
                                                </Col>
                                            </Row>
                                        </Card>

                                        {selectedSite.realtime && (
                                            <Card size="small" title="⚡ Dados em Tempo Real">
                                                <Row gutter={16}>
                                                    <Col span={12}>
                                                        <p><strong>PV Agora:</strong> {formatPower(selectedSite.realtime.pvPower)}</p>
                                                        <p><strong>Carga:</strong> {formatPower(selectedSite.realtime.loadPower)}</p>
                                                        <p><strong>Grid:</strong> {formatPower(selectedSite.realtime.meterPower)}</p>
                                                        {selectedSite.realtime.batteryPower !== undefined && (
                                                            <p><strong>Bateria:</strong> {formatPower(selectedSite.realtime.batteryPower)}</p>
                                                        )}
                                                    </Col>
                                                    <Col span={12}>
                                                        <p><strong>Geração Hoje:</strong> {formatEnergy(selectedSite.realtime.dailyGeneration)}</p>
                                                        <p><strong>Geração Total:</strong> {formatEnergy(selectedSite.realtime.totalGeneration, 0)}</p>
                                                        <p><strong>Receita Hoje:</strong> {formatCurrency(selectedSite.realtime.dailyIncome)}</p>
                                                        <p><strong>Receita Total:</strong> {formatCurrency(selectedSite.realtime.totalIncome)}</p>
                                                    </Col>
                                                </Row>
                                                {selectedSite.realtime.batterySoc1 && (
                                                    <>
                                                        <Divider />
                                                        <div style={{ padding: '12px', backgroundColor: '#e6f7ff', borderRadius: '4px' }}>
                                                            <Space size="large">
                                                                <div>
                                                                    <Text type="secondary" style={{ fontSize: '11px', display: 'block' }}>Bateria 1</Text>
                                                                    <Space>
                                                                        <span style={{ fontSize: '24px' }}>{getBatteryIcon(selectedSite.realtime.batterySoc1)}</span>
                                                                        <Text strong style={{ fontSize: '20px' }}>{selectedSite.realtime.batterySoc1}%</Text>
                                                                        <span style={{ fontSize: '16px' }}>{getBatteryStatusIcon(selectedSite.realtime.batteryStatus1)}</span>
                                                                    </Space>
                                                                </div>
                                                                {selectedSite.realtime.batterySoc2 && (
                                                                    <div>
                                                                        <Text type="secondary" style={{ fontSize: '11px', display: 'block' }}>Bateria 2</Text>
                                                                        <Space>
                                                                            <span style={{ fontSize: '24px' }}>{getBatteryIcon(selectedSite.realtime.batterySoc2)}</span>
                                                                            <Text strong style={{ fontSize: '20px' }}>{selectedSite.realtime.batterySoc2}%</Text>
                                                                            <span style={{ fontSize: '16px' }}>{getBatteryStatusIcon(selectedSite.realtime.batteryStatus2)}</span>
                                                                        </Space>
                                                                    </div>
                                                                )}
                                                            </Space>
                                                        </div>
                                                    </>
                                                )}
                                            </Card>
                                        )}

                                        <Card size="small" title="🔧 Capacidades e Tipo">
                                            <Row gutter={16}>
                                                <Col span={12}>
                                                    <p><strong>Tipo:</strong> {getPlantTypeLabel(selectedSite.plantType)}</p>
                                                    <p><strong>Capacidade PV:</strong> {selectedSite.pvCapacity || '-'} kWp</p>
                                                </Col>
                                                <Col span={12}>
                                                    {selectedSite.batteryCapacity && (
                                                        <p><strong>Capacidade Bateria:</strong> {selectedSite.batteryCapacity} kWh</p>
                                                    )}
                                                    {selectedSite.goodwePlantId && (
                                                        <p><strong>ID Goodwe:</strong> <Text code>{selectedSite.goodwePlantId}</Text></p>
                                                    )}
                                                </Col>
                                            </Row>
                                        </Card>
                                    </Space>
                                </TabPane>
                                
                                <TabPane tab="🔌 Equipamentos" key="equipments">
                                    {siteEquipments.length > 0 ? (
                                        <Table
                                            dataSource={siteEquipments}
                                            rowKey="id"
                                            columns={[
                                                { 
                                                    title: 'Identificação', 
                                                    dataIndex: 'name',
                                                    render: (name, record) => (
                                                        <Text strong>
                                                            {name || record.serialNumber || record.id}
                                                        </Text>
                                                    ),
                                                },
                                                {
                                                    title: 'Tipo',
                                                    dataIndex: 'type',
                                                    render: (type) => <Tag color="blue">{type}</Tag>,
                                                },
                                                {
                                                    title: 'Status',
                                                    dataIndex: 'status',
                                                    render: (status) => {
                                                        const isActive = status === 'CONECTADO';
                                                        return (
                                                            <Tag color={isActive ? 'green' : 'default'}>
                                                                {isActive ? '⚡' : '📶'} {status}
                                                            </Tag>
                                                        );
                                                    },
                                                },
                                                {
                                                    title: 'Fabricante',
                                                    dataIndex: 'manufacturer',
                                                    render: (manufacturer) => manufacturer ? (
                                                        <Tag color="purple">{manufacturer}</Tag>
                                                    ) : <Text type="secondary">-</Text>,
                                                },
                                            ]}
                                            pagination={false}
                                            size="small"
                                        />
                                    ) : (
                                        <div style={{ textAlign: 'center', padding: '40px' }}>
                                            <Text type="secondary">Nenhum equipamento cadastrado para este site</Text>
                                        </div>
                                    )}
                                </TabPane>
                            </Tabs>
                        </>
                    )}
                </Modal>

                {/* Modal de Criar Site - WIZARD */}
                <Modal
                    title="Cadastrar Novo Site"
                    open={createModalVisible}
                    onCancel={handleCancelWizard}
                    footer={null}
                    width={1200}
                    destroyOnClose
                    centered
                    bodyStyle={{ maxHeight: '70vh', overflowY: 'auto', padding: '24px' }}
                >
                    <SiteWizard
                        steps={[
                            {
                                title: 'Site',
                                description: 'Informações básicas',
                                content: <Step1SiteInfo />,
                            },
                            {
                                title: 'Solar',
                                description: 'Inversor/Usina',
                                content: <Step2Solar />,
                            },
                            {
                                title: 'Bateria',
                                description: 'Banco de baterias',
                                content: <Step3Bateria />,
                            },
                            {
                                title: 'Grid',
                                description: 'Concessionária',
                                content: <Step4Grid />,
                            },
                            {
                                title: 'Carregadores',
                                description: 'Estações EV',
                                content: <Step5Carregadores />,
                            },
                            {
                                title: 'Revisão',
                                description: 'Confirmar dados',
                                content: <Step6Review />,
                            },
                        ]}
                        onFinish={handleSubmitSite}
                        onCancel={handleCancelWizard}
                    />
                </Modal>
            </div>
        </BasePage>
    );
};

export default MapPage;
